/**
 * Class constants for the editing surface.
 *
 * Colours are semantic tokens only (`ml/no-foreign-colour-utility`). Sizes are
 * named text-scale steps; the only measured values are the host-supplied
 * `minHeight`/`maxHeight`, applied as inline style because they are data.
 */

/**
 * Typography for documents, shared by the editor and the read-only display so a
 * stored value reads the way it was written.
 *
 * Descendant selectors rather than a typography plugin: the schema is closed
 * (h2/h3, lists, quote, rule, table, image, marks), so the set of elements is
 * known and small. Colours are tokens; sizes are named steps.
 */
export const richTextProseStyles = [
  'text-sm text-content',
  '[&_h2]:text-xl [&_h2]:font-semibold [&_h3]:text-lg [&_h3]:font-semibold',
  '[&_p]:my-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5',
  '[&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-border-strong [&_blockquote]:pl-3 [&_blockquote]:text-content-muted',
  '[&_a]:text-accent [&_a]:underline',
  '[&_code]:rounded [&_code]:bg-surface-sunken [&_code]:px-1 [&_pre]:my-2 [&_pre]:rounded [&_pre]:bg-surface-sunken [&_pre]:p-3',
  '[&_hr]:my-3 [&_hr]:border-border',
  '[&_table]:my-2 [&_table]:w-full [&_table]:border-collapse',
  '[&_td]:border [&_td]:border-border [&_td]:p-1.5',
  '[&_th]:border [&_th]:border-border [&_th]:bg-surface-sunken [&_th]:p-1.5 [&_th]:text-left',
  '[&_img]:my-2 [&_img]:max-w-full [&_img]:rounded',
].join(' ');

export const richTextDisplayStyles = {
  root: 'text-content',
  content: 'text-sm',
  empty: 'text-sm text-content-subtle',
} as const;

export const richTextEditorStyles = {
  root: 'flex w-full flex-col gap-1.5',

  /** The writing area: it scrolls internally past `maxHeight`. */
  surface:
    'rounded-control border border-border bg-surface-raised transition-colors hover:border-border-strong focus-within:border-border-strong',

  surfaceInvalid: 'border-critical',

  surfaceDisabled: 'cursor-not-allowed bg-surface-sunken',

  /** Applied to the ProseMirror root through `EditorContent`. */
  content: 'px-3 py-2 outline-none',

  error: 'text-xs text-critical',
} as const;

export const richTextToolbarStyles = {
  /** One row above the writing area; it wraps rather than overflowing. */
  row: 'flex flex-wrap items-center gap-1 border-b border-border px-1.5 py-1',

  /** The contextual row that appears inside a table. */
  rowSecondary:
    'flex flex-wrap items-center gap-1 border-b border-border bg-surface-sunken px-1.5 py-1',

  /** The toolbar stack: the fixed row plus any contextual row. */
  stack: 'flex flex-col',

  group: 'flex items-center gap-0.5',

  divider: 'mx-1 h-5 w-px bg-border',

  /** A pressed toggle is a state, and it is also announced by `aria-pressed`. */
  controlActive: 'bg-accent-soft text-accent',

  /** Small text accompanying a control group. */
  label: 'px-1 text-xs text-content-muted',

  /** The link editor's field and its submit control. */
  popoverForm: 'mt-3 flex items-end gap-2',
} as const;
